import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ROLE_LABEL } from "@/constants/roles";
import { useAuth } from "@/contexts/AuthContext";
import { useSearch } from "@/contexts/SearchContext";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";

const ROLES = Object.keys(ROLE_LABEL);

export default function Users() {
  const { user } = useAuth();
  const { t } = useT();
  const { query, setPlaceholder } = useSearch();
  const qc = useQueryClient();
  const { data: rawData = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "back_office" });
  const create = useMutation({
    mutationFn: (d) => api.post("/auth/register", d),
    onSuccess: () => { qc.invalidateQueries(); setOpen(false); toast.success("User created"); setForm({ email: "", password: "", name: "", role: "back_office" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  useEffect(() => {
    setPlaceholder("Search users — Name · Email · Role…");
  }, [setPlaceholder]);

  const data = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return rawData;
    return rawData.filter((u) => [u.name, u.email, u.role, ROLE_LABEL[u.role]]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [rawData, query]);

  if (user.role !== "super_admin") return <div className="text-sm text-slate-500">Super Admin only.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("Users")}</h1>
          <p className="text-sm text-slate-500 mt-1">{data.length} accounts.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="bg-slate-900 hover:bg-slate-800">+ New User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-xs">Email</Label><Input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label className="text-xs">Password</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{t(ROLE_LABEL[r])}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="user-create-submit" className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => create.mutate(form)}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Name", "Email", "Role", "Created"].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 font-medium">{t(ROLE_LABEL[u.role])}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
